import React from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineAcademicCap,
  HiOutlineGlobeAlt,
  HiOutlineHeart,
  HiOutlineLightBulb,
  HiOutlineClipboardCheck,
  HiOutlineBadgeCheck,
} from "react-icons/hi";
import PageHero from "./PageHero";

const highlights = [
  {
    value: "2013",
    label: "School opened with Master of General Surgery",
    tone: "#0863AA",
  },
  {
    value: "1,000+",
    label: "Professionals awarded postgraduate degrees",
    tone: "#0d9488",
  },
  {
    value: "2024",
    label: "First PhD in Private Law awarded",
    tone: "#0a3d6b",
  },
  {
    value: "8+",
    label: "Partner universities across Africa, Asia, and Europe",
    tone: "#5eb3d4",
  },
];

const pillars = [
  {
    title: "Our vision",
    body: "To become a leading postgraduate school known for research, for preparing students to lead in their fields, and for a community that can work across disciplines on complex problems.",
    Icon: HiOutlineLightBulb,
  },
  {
    title: "Our mission",
    body: "To train highly skilled graduates for professional practice and advanced research, and to offer research-oriented education that helps scholars serve their communities with integrity.",
    Icon: HiOutlineAcademicCap,
  },
  {
    title: "Core values",
    body: "Accountability, transparency, empowerment, and legitimacy. Students are expected to act with courage, to know the limits of their knowledge and authority, and to practise self-criticism.",
    Icon: HiOutlineHeart,
  },
  {
    title: "Code of ethics",
    body: "Postgraduate students keep the university’s reputation through academic integrity, personal responsibility, and mutual respect — and are asked to uphold those standards actively.",
    Icon: HiOutlineBadgeCheck,
  },
];

const partners = [
  "Istanbul Medeniyet University",
  "Ondokuz Mayis University",
  "Makerere University",
  "International University of Africa",
  "Mbarara University of Science and Technology",
  "University of Nairobi",
  "Eastern Mediterranean University",
  "Doctors Worldwide",
];

const hospitals = [
  "Banadir Hospital, Mogadishu",
  "Madina Hospital, Mogadishu",
  "Somalia–Türkiye Recep Tayyip Erdoğan Training and Research Hospital, Mogadishu",
  "Göztepe Prof. Dr. Süleyman Yalçın City Hospital, Istanbul",
  "Arua, Mbale, Jinja, Lira, Fort Portal, and Masaka Regional Referral Hospitals, Uganda",
];

const grades = [
  ["90–100", "A+", "5", "Exceptional"],
  ["80–89", "A", "5", "Excellent"],
  ["75–79", "B+", "4.5", "Very good"],
  ["70–74", "B", "4", "Good"],
  ["65–69", "C+", "3.5", "Fairly good"],
  ["60–64", "C", "3", "Pass"],
  ["Below 60", "D+ to F", "2.5–0", "Fail"],
];

const applyItems = [
  "A completed application form",
  "Official transcripts and certified bachelor’s certificates",
  "An updated CV",
  "A statement of purpose",
  "Two or three recommendation letters",
  "Proof of English if earlier study was not in English",
  "An entrance examination or interview, where required",
];

const PostgradAbout = () => {
  return (
    <div className="bg-[#f3f7fb]">
      <PageHero
        kicker="Handbook 2025"
        title="School of Postgraduate Studies"
        subtitle="Who we are, how admission works, and how courses are graded — written for applicants before they choose a programme."
      />

      <section className="pg-section-band relative overflow-hidden py-16">
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-end gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="pg-animate-slide max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0863AA]">
                Since March 2013
              </p>
              <h2 className="mt-3 text-3xl font-bold text-[#0a3d6b] sm:text-4xl">
                Shape your future with us
              </h2>
              <div className="mt-5 space-y-4 text-lg leading-relaxed text-slate-700">
                <p>
                  Benadir University opened the School of Postgraduate Studies
                  and Research to oversee graduate study and research. The first
                  programme was the Master of General Surgery, developed with
                  Istanbul Medeniyet University. The school has since grown into
                  one of Somalia&apos;s larger postgraduate communities.
                </p>
                <p>
                  Study here strengthens critical thinking, research, and
                  professional judgement. Senior lecturers and professors teach
                  to a high standard, in class and in the community, on problems
                  that matter in Somalia.
                </p>
              </div>
            </div>

            <aside className="pg-animate-in-delay pg-quote">
              <p className="text-2xl font-bold leading-snug text-[#0a3d6b]">
                “This is your time. This is your university.”
              </p>
              <p className="mt-3 text-sm text-slate-600">
                Teaching is built to move a career forward — with support for
                health, wellbeing, and personal development alongside the
                degree.
              </p>
            </aside>
          </div>

          <div className="pg-stagger mt-12 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {highlights.map((item) => (
              <article
                key={item.value}
                className="pg-card relative overflow-hidden border border-sky-100 bg-white p-5"
              >
                <div
                  className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-20"
                  style={{ background: item.tone }}
                  aria-hidden
                />
                <p
                  className="text-3xl font-extrabold tracking-tight"
                  style={{ color: item.tone }}
                >
                  {item.value}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  {item.label}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="pg-animate-in mb-8 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0863AA]">
              What guides us
            </p>
            <h2 className="mt-2 text-3xl font-bold text-[#0a3d6b]">
              Vision, mission, values, and ethics
            </h2>
          </div>

          <div className="pg-stagger grid gap-5 md:grid-cols-2">
            {pillars.map(({ title, body, Icon }) => (
              <article key={title} className="pg-card pg-feature-tile">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center bg-[#0863AA] text-white">
                  <Icon size={22} />
                </div>
                <h3 className="text-xl font-bold text-[#0a3d6b]">{title}</h3>
                <p className="mt-3 leading-relaxed text-slate-700">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(94,179,212,0.18),transparent_35%)]" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="pg-animate-in flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0863AA]">
                Global reach
              </p>
              <h2 className="mt-2 text-3xl font-bold text-[#0a3d6b]">
                Partner universities
              </h2>
              <p className="mt-3 max-w-2xl text-slate-700">
                Joint programmes connect students with universities in Africa,
                Asia, and Europe.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-[#0863AA]">
              <HiOutlineGlobeAlt size={22} />
              <span className="text-sm font-semibold">8+ partnerships</span>
            </div>
          </div>

          <ul className="pg-stagger mt-8 grid gap-3 sm:grid-cols-2">
            {partners.map((name) => (
              <li
                key={name}
                className="pg-card group flex items-center gap-3 border border-sky-100 bg-white px-4 py-4"
              >
                <span className="h-2.5 w-2.5 shrink-0 bg-[#5eb3d4] transition group-hover:scale-125" />
                <span className="font-medium text-slate-800">{name}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-2">
          <article className="pg-animate-in pg-card border border-sky-100 bg-[#f8fbfe] p-7">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center bg-[#0863AA] text-white">
              <HiOutlineClipboardCheck size={22} />
            </div>
            <h2 className="text-2xl font-bold text-[#0a3d6b]">How to apply</h2>
            <p className="mt-3 leading-relaxed text-slate-700">
              Admission is competitive. It looks at academic record,
              professional experience, and potential for leadership. Exact
              requirements differ by programme. Most applicants prepare:
            </p>
            <ul className="mt-5 space-y-3">
              {applyItems.map((item, index) => (
                <li key={item} className="flex gap-3 text-slate-700">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center bg-[#0863AA] text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="pg-animate-in-delay pg-card border border-sky-100 bg-white p-7">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center bg-[#0a3d6b] text-white">
              <HiOutlineBadgeCheck size={22} />
            </div>
            <h2 className="text-2xl font-bold text-[#0a3d6b]">How you graduate</h2>
            <p className="mt-3 leading-relaxed text-slate-700">
              A master&apos;s candidate completes the required coursework, any
              clinical or practical placements, and a thesis or capstone. The
              pass mark in a course is 60%. Candidates also follow the code of
              conduct and clear university fees.
            </p>
            <p className="mt-4 leading-relaxed text-slate-700">
              Doctoral candidates have additional examinations and a
              dissertation defence, set out on each PhD page.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-sky-100 bg-[#f3f7fb] p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#0863AA]">
                  Intakes
                </p>
                <p className="mt-1 font-bold text-[#0a3d6b]">January · September</p>
              </div>
              <div className="border border-sky-100 bg-[#f3f7fb] p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#0863AA]">
                  Semester
                </p>
                <p className="mt-1 font-bold text-[#0a3d6b]">4 months</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="pg-section-band relative py-16">
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="pg-animate-in mb-6 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#0863AA]">
              Academic standards
            </p>
            <h2 className="mt-2 text-3xl font-bold text-[#0a3d6b]">
              How courses are graded
            </h2>
            <p className="mt-3 text-slate-700">
              Each course is marked out of 100. The pass mark is 60%.
            </p>
          </div>

          <div className="pg-animate-in-delay overflow-hidden border border-sky-100 bg-white shadow-[0_16px_40px_rgba(10,61,107,0.08)]">
            <table className="pg-grade-table min-w-full text-left text-sm">
              <thead className="bg-[#0863AA] text-white">
                <tr>
                  <th className="px-4 py-3.5 font-semibold">Marks</th>
                  <th className="px-4 py-3.5 font-semibold">Grade</th>
                  <th className="px-4 py-3.5 font-semibold">Points</th>
                  <th className="px-4 py-3.5 font-semibold">Meaning</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((row) => (
                  <tr key={row[0]} className="border-t border-slate-100">
                    {row.map((cell) => (
                      <td key={cell} className="px-4 py-3.5 text-slate-800">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="pg-cta-glow py-16 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="pg-animate-in max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-200">
              Clinical training
            </p>
            <h2 className="mt-2 text-3xl font-bold">Training hospitals</h2>
            <p className="mt-3 text-sky-100">
              Medicine residencies rotate through Benadir teaching hospitals and
              partner hospitals in Türkiye and Uganda.
            </p>
          </div>

          <ul className="pg-stagger mt-8 grid gap-3 sm:grid-cols-2">
            {hospitals.map((name) => (
              <li
                key={name}
                className="border border-white/20 bg-white/10 px-4 py-4 backdrop-blur-sm transition hover:bg-white/15"
              >
                {name}
              </li>
            ))}
          </ul>

          <div className="pg-animate-in-delay-2 mt-10 flex flex-wrap gap-3">
            <Link
              to="/academics/postgraduate/programs"
              className="bg-white px-6 py-3 font-semibold text-[#0863AA] transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              View programmes
            </Link>
            <Link
              to="/academics/postgraduate"
              className="border-2 border-white px-6 py-3 font-semibold text-white transition hover:bg-white/10"
            >
              Dean&apos;s message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostgradAbout;
